import React from "react";
import { AlertOctagon, RotateCw, Home } from "lucide-react";
import { logger } from "../../utill/logger";
import "../notificationAlert/notifications.scss";

// App-wide error boundary. Catches render-time exceptions in any descendant component
// (the alternative is a blank white page when something throws below the router).
//
// Two recovery actions:
//   - "Reload" hard-refreshes the page (cheap, fixes most transient render bugs)
//   - "Go home" routes back to the schedules page without losing the auth session
//
// Technical details collapse into a <details> so support can ask the user to expand
// and copy the stack — but the stack is hidden by default to avoid scaring users.
//
// Note: ErrorBoundary catches RENDER errors only — not async failures from fetch /
// Promise rejections. Those still go through the existing toUserError + InlineAlert /
// ToastAlert pipeline.

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null, info: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // logger.error always emits, even in production — this is exactly the signal that
    // monitoring (Sentry / Datadog) should pick up. We avoid console.error directly so
    // the existing logger gating stays the single integration point.
    logger.error("Render-time error caught by ErrorBoundary:", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.assign("/schedules");
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, info } = this.state;
    const technicalDetails = [
      error && error.toString(),
      info && info.componentStack,
    ]
      .filter(Boolean)
      .join("\n\n");

    return (
      <div className="error-boundary-fallback" role="alert">
        <div className="error-boundary-fallback__card">
          <div className="error-boundary-fallback__icon">
            <AlertOctagon size={28} aria-hidden="true" />
          </div>
          <h1 className="error-boundary-fallback__title">Something went wrong</h1>
          <p className="error-boundary-fallback__subtitle">
            The page hit an unexpected error. Reloading usually fixes it. If it
            keeps happening, share the technical details below with the support team.
          </p>
          <div className="error-boundary-fallback__actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={this.handleReload}
            >
              <RotateCw size={16} className="me-2" aria-hidden="true" />
              Reload page
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={this.handleHome}
            >
              <Home size={16} className="me-2" aria-hidden="true" />
              Go to dashboard
            </button>
          </div>
          {technicalDetails && (
            <details className="error-boundary-fallback__details">
              <summary>Technical details (for support)</summary>
              <pre>{technicalDetails}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
